import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Stack, DownloadSimple, Crown, ArrowLeft, Lock, Check } from "@phosphor-icons/react";

const typeLabel = { icon_pack: "Icon Pack", widget: "Widget", wallpaper: "Wallpaper" };

export default function BundleDetail() {
  const { id } = useParams();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get(`/bundles/${id}`).then((r) => setBundle(r.data)).catch(() => setBundle(false));
  }, [id]);

  const handleDownload = async () => {
    if (!user) return login();
    if (!user.is_premium && !user.is_admin) {
      toast.error("Premium required", { description: "Subscribe to download the full bundle." });
      return navigate("/premium");
    }
    setDownloading(true);
    try {
      const res = await api.post(`/bundles/${id}/download`);
      toast.success("Bundle unlocked!", { description: `${res.data.count} pieces added to your library.` });
      setBundle((p) => ({ ...p, download_count: p.download_count + 1 }));
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Premium required");
        navigate("/premium");
      } else {
        toast.error("Something went wrong");
      }
    } finally {
      setDownloading(false);
    }
  };

  if (bundle === false) return <div className="max-w-[1400px] mx-auto px-5 py-32 text-center text-white/50">Bundle not found.</div>;
  if (!bundle) return <div className="max-w-[1400px] mx-auto px-5 py-32 text-center text-white/50">Loading…</div>;

  const canDownload = user && (user.is_premium || user.is_admin);

  return (
    <div className="grid-bg min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-8">
          <ArrowLeft size={18} /> Back
        </button>

        {/* Hero */}
        <div className="relative rounded-2xl overflow-hidden mb-12">
          <img src={bundle.cover_image} alt={bundle.title} className="w-full h-64 lg:h-80 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/50 to-transparent" />
          <div className="absolute bottom-0 left-0 p-8 lg:p-10">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#05050A] bg-[#FFEB3B] px-2.5 py-1 rounded-sm mb-4">
              <Stack weight="fill" size={12} /> {bundle.items.length}-piece bundle
            </span>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter uppercase leading-none">{bundle.title}</h1>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-10">
          {/* Items in bundle */}
          <div className="lg:col-span-2">
            <p className="text-white/70 text-lg leading-relaxed mb-8">{bundle.description}</p>
            <h2 className="font-display font-bold text-xl uppercase tracking-tight mb-5">What's inside</h2>
            <div className="grid sm:grid-cols-3 gap-4" data-testid="bundle-items">
              {bundle.items.map((it) => (
                <Link key={it.id} to={`/item/${it.id}`} className="group rounded-lg overflow-hidden glass hover:border-white/30 transition-all hover:-translate-y-1">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={it.preview_image} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-3">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#00E5FF]">{typeLabel[it.type]}</span>
                    <p className="font-bold text-sm mt-1 truncate">{it.title}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Download card */}
          <div>
            <div className="glass rounded-lg p-6 sticky top-24">
              <div className="flex items-center gap-2 text-[#FFEB3B] mb-3">
                <Crown weight="fill" size={20} />
                <span className="font-bold uppercase tracking-wider text-sm">Premium Bundle</span>
              </div>
              <ul className="space-y-2.5 mb-6 mt-4">
                {bundle.items.map((it) => (
                  <li key={it.id} className="flex items-center gap-2 text-white/70 text-sm">
                    <Check weight="bold" size={14} className="text-[#00E5FF] shrink-0" />
                    <span className="truncate">{it.title}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-1 text-white/40 text-xs mb-4">
                <DownloadSimple size={14} /> {bundle.download_count} downloads
              </div>
              <button
                data-testid="download-bundle-btn"
                onClick={handleDownload}
                disabled={downloading}
                className={`w-full flex items-center justify-center gap-2 font-bold py-4 rounded-sm transition-all disabled:opacity-60 ${
                  canDownload
                    ? "bg-[#FFEB3B] text-[#05050A] hover:bg-[#fff35c] hover:shadow-[0_0_28px_rgba(255,235,59,0.5)]"
                    : "bg-white/10 text-white hover:bg-white/15 border border-white/20"
                }`}
              >
                {canDownload ? (
                  <><DownloadSimple weight="bold" size={20} /> {downloading ? "Preparing…" : "Download bundle"}</>
                ) : (
                  <><Lock weight="fill" size={18} /> Unlock with Premium</>
                )}
              </button>
              {!user && (
                <p className="text-white/40 text-xs text-center mt-4">
                  <button onClick={login} className="text-[#00E5FF] hover:underline">Sign in</button> to download
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
